/**
 * @module app.ext.mcg-informed
 */

import { autoinject } from 'aurelia-framework';
import { AssessmentContext } from 'app/ext/mcg-informed/assessment-context';
import { IQuestionComponent, IQuestionConfig } from 'app/ext/mcg-informed/interfaces';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { QuestionBase } from 'app/ext/mcg-informed/question-base';
import { McgInformedDataMethods as mcgMethods } from 'app/ext/mcg-informed/data-methods';
import * as Mcg from 'app/ext/mcg-informed/interfaces';
import { guid } from 'app/shared/lib/guid';

@autoinject
export class QuestionRadioCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties **************************************////

    protected selectedChoice: Mcg.Choice | null = null;
    protected selectedGuidelineLinks: Mcg.GuidelineLink[] = [];
    protected existingAttachments: string[] = [];
    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) ******************////

    constructor(
        private readonly element: Element,
        protected readonly context: AssessmentContext,
        private readonly logger: AppLogger) {

        super(context);
        this.logger.source = this;

        if(!context || !context.currentSession) throw new Error("No Assessment context is available");

        if(!this.context.questionComponents.includes(this)) this.context.questionComponents.push(this);
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        this.question = config.question;
        this.questionNumberDisplay = config.questionNumberDisplay;
        this.readonly = config.readonly;
        this.existingAttachments = config.existingAttachments;
        this.labelId = config.labelId;
    }

    protected attached(): void {
        this.loadPreviousAnswer();
    }

    ////****************** Events ***********************************************////

    protected choiceChanged(choice: Mcg.Choice | null): void {
        this.isValid = (!!choice && !!choice.id);
        this.context.unsavedChanges = true;

        this.context.questionComponents.forEach(qc => {
            if(!qc.question) return;
            qc.question.answersSet.forEach(a => {
                if(a.uid === choice?.uid) mcgMethods.hideFollowupsRecursive(qc.question, this.context);
            });
        });

        if(choice && choice.questionRefSet?.length > 0) {
            choice.questionRefSet.forEach(qr => {
                this.context.selectedQuestionReferenceIds += `|${qr.id}`;
            });
        }
    }

    protected guidelineLinkChoiceChanged(): void {
        this.context.unsavedChanges = true;
    }

    ////****************** Functions ****************************************************////

    private async loadPreviousAnswer(): Promise<void> {
        if(!this.question || !this.context.attachment.currentAnswers) return;

        try {
            this.selectedChoice = this.question.answersSet.find(a => a.selected) || null;
            if(this.selectedChoice && this.selectedChoice.questionRefSet?.length > 0) {
                this.selectedChoice.questionRefSet.forEach(qr => {
                    this.context.selectedQuestionReferenceIds += `|${qr.id}`;
                });
            }
        }
        catch(error) {
            const questionId = this.question && this.question.id;
            const description = this.question && this.question.questionText || '';

            this.logger.error(`Unable to find existing answer for question ${questionId} (${description})`);
            throw error;
        }
    }

    ////****************** IQuestionComponent ****************************************************////

    public saveAnswer = null;  // Not Implemented

    public getAnswers(): Mcg.Choice[] {
        return this.selectedChoice ? new Array<Mcg.Choice>(this.selectedChoice) : [];
    }

    public getGuidelineLinks(): Mcg.GuidelineLink[] {
        return this.selectedGuidelineLinks;
    }

    public clearSelectedAnswers(): void {
        this.selectedChoice = null;
    }

}
